/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


const A_FIRST = -1;
const B_FIRST = 1;


/*******************************************************************************
 ** comparator for listing labels that matched a search: labels that start with
 ** the whole search come first, then labels that start with its first word,
 ** then the rest - each group alphabetically.  Extra spaces, in the search or
 ** in the labels, are ignored for the grouping, the same as matchesSearchWords
 ** ignores them.
 *******************************************************************************/
export function compareLabelsForSearch(labelA: string, labelB: string, rawSearch: string): number
{
   const search = splitWords(rawSearch).join(" ");
   if (search != "")
   {
      const normalizedA = splitWords(labelA).join(" ");
      const normalizedB = splitWords(labelB).join(" ");

      let aStartsWith = normalizedA.startsWith(search);
      let bStartsWith = normalizedB.startsWith(search);

      if (aStartsWith && !bStartsWith)
      {
         return A_FIRST;
      }
      else if (bStartsWith && !aStartsWith)
      {
         return B_FIRST;
      }

      const indexOfSpace = search.indexOf(" ");
      if (indexOfSpace > 0)
      {
         aStartsWith = normalizedA.startsWith(search.substring(0, indexOfSpace));
         bStartsWith = normalizedB.startsWith(search.substring(0, indexOfSpace));

         if (aStartsWith && !bStartsWith)
         {
            return A_FIRST;
         }
         else if (bStartsWith && !aStartsWith)
         {
            return B_FIRST;
         }
      }
   }

   return (labelA.localeCompare(labelB));
}


/*******************************************************************************
 ** does a value (e.g., a label) match what the user typed into a search box?
 **
 ** A single search word matches if it appears anywhere in the value.  Several
 ** search words match if each one appears inside its own word of the value, in
 ** the same order - so "fo ba" matches "Foo Bar", but "fo oo" does not (both
 ** would need the one word "Foo").  Case, and extra spaces, are ignored.
 *******************************************************************************/
export function matchesSearchWords(value: string, search: string): boolean
{
   ////////////////////////////////////////////////////////////////////////
   // split on runs of whitespace, so extra spaces make no empty "words" //
   ////////////////////////////////////////////////////////////////////////
   const searchParts = splitWords(search);
   if (searchParts.length == 0)
   {
      return (true);
   }

   if (searchParts.length == 1)
   {
      //////////////////////////////////////////////
      // if only 1 word, just do an includes test //
      //////////////////////////////////////////////
      return (value.toLowerCase().includes(searchParts[0]));
   }

   ////////////////////////////////////////
   // else split the value on spaces too //
   ////////////////////////////////////////
   const valueParts = splitWords(value);
   if (searchParts.length > valueParts.length)
   {
      //////////////////////////////////////////////////////////////////////////////////
      // if there are more words in the search than in the value, then it can't match //
      // e.g. "order c" can't ever match, say "order"                                 //
      //////////////////////////////////////////////////////////////////////////////////
      return (false);
   }

   /////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // iterate over the search parts - if any don't match the corresponding value parts, then it's a non-match //
   // a value word that matches is used up, so the next search word has to match a later value word.          //
   /////////////////////////////////////////////////////////////////////////////////////////////////////////////
   let valueIndex = 0;
   for (let i = 0; i < searchParts.length; i++)
   {
      let foundMatch = false;
      for (; valueIndex < valueParts.length; valueIndex++)
      {
         if (valueParts[valueIndex].includes(searchParts[i]))
         {
            foundMatch = true;
            valueIndex++;
            break;
         }
      }

      if (!foundMatch)
      {
         return (false);
      }
   }

   /////////////////////////////////
   // if no failure, return a hit //
   /////////////////////////////////
   return (true);
}


/*******************************************************************************
 ** lower-cased words of a string, split on runs of whitespace (none, for a
 ** blank string).
 *******************************************************************************/
function splitWords(text: string): string[]
{
   return (text.toLowerCase().split(/\s+/).filter(word => word != ""));
}
